import React, { useState, useEffect, useCallback } from "react";
import API_BASE from "../apiConfig";
import { useTheme, getT } from "../ThemeContext";
import { useAppConfig } from "../AppContext";
import { useSort } from "../hooks/useSort";
import { useDateTimeFormat } from "../utils/formatters";
import { PrimaryButton, GhostButton } from "../components/ThemedButton";

const money = (n, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(n || 0));

async function api(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.message || `Request failed (${res.status})`);
  }
  return data;
}

const PRODUCT_TYPES = ["physical", "service", "membership", "program"];

const emptyProduct = {
  sku: "", barcode: "", name: "", description: "", category_id: "", brand: "", vendor: "",
  product_type: "physical", image_url: "", cost_price: 0, selling_price: 0, tax_rate_id: "",
  track_inventory: true, reorder_level: 0, weight: "", taxable: "",
};

const emptyVariant = { variant_name: "", sku: "", barcode: "", price: "", cost: "", weight: "" };

export default function POSCatalog({ currentUser }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const { config } = useAppConfig();
  const currency = (config?.currency || "USD").toUpperCase();
  const { fmtDateTime } = useDateTimeFormat();

  // Mutating endpoints (create/update/deactivate/add-variant/add-category) require
  // Admin, Super Admin, or Staff — see routers/pos_catalog.py's `_admin` dependency
  // (require_role("Admin", "Super Admin", "Staff")). Reads only require any valid session.
  const canWrite = ["Admin", "Super Admin", "Staff"].includes(currentUser?.user_role);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);

  const [editing, setEditing] = useState(null); // product object (with .variants) or null
  const [form, setForm] = useState(emptyProduct);
  const [modalOpen, setModalOpen] = useState(false);
  const [variantForm, setVariantForm] = useState(emptyVariant);
  const [addingVariant, setAddingVariant] = useState(false);

  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryParent, setNewCategoryParent] = useState("");
  const [newCategoryTaxable, setNewCategoryTaxable] = useState(true);
  const [categoryPanelOpen, setCategoryPanelOpen] = useState(false);

  const { sorted, sortKey, sortDir, toggleSort, sortArrow } = useSort(products, "name");

  const loadCategories = useCallback(() => {
    api("/pos/catalog/categories")
      .then((d) => setCategories(d.categories || []))
      .catch((e) => setError(e.message));
  }, []);

  const loadProducts = useCallback(() => {
    setLoading(true); setError("");
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (categoryFilter) params.set("category_id", categoryFilter);
    params.set("active_only", activeOnly ? "true" : "false");
    params.set("page_size", "200");
    api(`/pos/catalog/products?${params.toString()}`)
      .then((d) => setProducts(d.products || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [query, categoryFilter, activeOnly]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => {
    api("/pos/tax/rates").then((d) => setTaxRates(d.tax_rates || [])).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setTimeout(() => loadProducts(), 200);
    return () => clearTimeout(t);
  }, [loadProducts]);

  const categoryName = (id) => categories.find((c) => c.category_id === id)?.name || "—";

  function openNew() {
    setEditing(null);
    setForm(emptyProduct);
    setVariantForm(emptyVariant);
    setAddingVariant(false);
    setModalOpen(true);
  }

  async function openEdit(productSummary) {
    setError("");
    try {
      const d = await api(`/pos/catalog/products/${productSummary.product_id}`);
      setEditing(d.product);
      setForm({
        sku: d.product.sku || "", barcode: d.product.barcode || "", name: d.product.name || "",
        description: d.product.description || "", category_id: d.product.category_id || "",
        brand: d.product.brand || "", vendor: d.product.vendor || "",
        product_type: d.product.product_type || "physical", image_url: d.product.image_url || "",
        cost_price: d.product.cost_price ?? 0, selling_price: d.product.selling_price ?? 0,
        tax_rate_id: d.product.tax_rate_id || "", track_inventory: !!d.product.track_inventory,
        reorder_level: d.product.reorder_level ?? 0, weight: d.product.weight ?? "",
        taxable: d.product.taxable === null || d.product.taxable === undefined ? "" : (d.product.taxable ? "1" : "0"),
      });
      setVariantForm(emptyVariant);
      setAddingVariant(false);
      setModalOpen(true);
    } catch (e) { setError(e.message); }
  }

  function closeModal() {
    setModalOpen(false); setEditing(null); setForm(emptyProduct);
  }

  function buildPayload() {
    return {
      sku: form.sku || null, barcode: form.barcode || null, name: form.name,
      description: form.description || null,
      category_id: form.category_id ? Number(form.category_id) : null,
      brand: form.brand || null, vendor: form.vendor || null, product_type: form.product_type,
      image_url: form.image_url || null,
      cost_price: Number(form.cost_price) || 0, selling_price: Number(form.selling_price) || 0,
      tax_rate_id: form.tax_rate_id ? Number(form.tax_rate_id) : null,
      track_inventory: !!form.track_inventory,
      reorder_level: Number(form.reorder_level) || 0,
      weight: form.weight === "" ? null : Number(form.weight),
      taxable: form.taxable === "" ? null : form.taxable === "1",
    };
  }

  async function saveProduct() {
    if (!form.name.trim()) { setError("Product name is required."); return; }
    setBusy(true); setError("");
    try {
      if (editing) {
        await api(`/pos/catalog/products/${editing.product_id}`, {
          method: "PUT", body: JSON.stringify(buildPayload()),
        });
      } else {
        await api("/pos/catalog/products", { method: "POST", body: JSON.stringify(buildPayload()) });
      }
      closeModal();
      loadProducts();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  async function deactivateProduct(product) {
    if (!window.confirm(`Deactivate "${product.name}"? It will no longer be sellable at the register.`)) return;
    setBusy(true); setError("");
    try {
      await api(`/pos/catalog/products/${product.product_id}/deactivate`, { method: "POST" });
      loadProducts();
      if (editing?.product_id === product.product_id) closeModal();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  async function addVariant() {
    if (!editing) return;
    if (!variantForm.variant_name.trim()) { setError("Variant name is required."); return; }
    setBusy(true); setError("");
    try {
      await api(`/pos/catalog/products/${editing.product_id}/variants`, {
        method: "POST",
        body: JSON.stringify({
          variant_name: variantForm.variant_name,
          sku: variantForm.sku || null,
          barcode: variantForm.barcode || null,
          price: variantForm.price === "" ? null : Number(variantForm.price),
          cost: variantForm.cost === "" ? null : Number(variantForm.cost),
          weight: variantForm.weight === "" ? null : Number(variantForm.weight),
        }),
      });
      const d = await api(`/pos/catalog/products/${editing.product_id}`);
      setEditing(d.product);
      setVariantForm(emptyVariant);
      setAddingVariant(false);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  async function addCategory() {
    if (!newCategoryName.trim()) return;
    setBusy(true); setError("");
    try {
      await api("/pos/catalog/categories", {
        method: "POST",
        body: JSON.stringify({
          name: newCategoryName,
          parent_category_id: newCategoryParent ? Number(newCategoryParent) : null,
          taxable: newCategoryTaxable,
        }),
      });
      setNewCategoryName(""); setNewCategoryParent(""); setNewCategoryTaxable(true);
      loadCategories();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  async function toggleCategoryTaxable(cat) {
    setBusy(true); setError("");
    try {
      await api(`/pos/catalog/categories/${cat.category_id}`, {
        method: "PUT",
        body: JSON.stringify({
          name: cat.name, parent_category_id: cat.parent_category_id, taxable: !cat.taxable,
        }),
      });
      loadCategories();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  const card = { background: T.glass, border: `1px solid ${T.glassBdr}`, borderRadius: 14, padding: 16 };
  const input = { width: "100%", padding: 8, borderRadius: 8, boxSizing: "border-box", border: `1px solid ${T.inputBdr}`, background: T.inputBg, color: T.inputText };
  const label = { color: T.textSub, fontSize: 12, display: "block", marginTop: 10, marginBottom: 4 };
  const th = (key, text) => (
    <th onClick={() => toggleSort(key)} style={{ textAlign: "left", padding: "8px 10px", cursor: "pointer", userSelect: "none", color: sortKey === key ? T.blue : T.textSub, fontSize: 12, borderBottom: `1px solid ${T.divider}` }}>
      {text}{sortArrow(key)}
    </th>
  );

  return (
    <div style={{ color: T.textPrimary }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ margin: 0 }}>Product Catalog</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <GhostButton onClick={() => setCategoryPanelOpen((v) => !v)}>
            {categoryPanelOpen ? "Hide Categories" : "Manage Categories"}
          </GhostButton>
          <PrimaryButton
            disabled={!canWrite}
            title={canWrite ? undefined : "Requires Admin, Super Admin, or Staff role"}
            onClick={openNew}
          >
            + New Product
          </PrimaryButton>
        </div>
      </div>

      {error && <div style={{ color: T.red, marginBottom: 12 }}>{error}</div>}

      {categoryPanelOpen && (
        <div style={{ ...card, marginBottom: 16 }}>
          <strong>Categories</strong>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "10px 0" }}>
            {categories.map((c) => (
              <span key={c.category_id} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, background: T.tabActiveBg, color: T.tabActiveClr, padding: "4px 10px", borderRadius: 8 }}>
                {c.name}
                <label style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 10, opacity: 0.85, cursor: canWrite ? "pointer" : "default" }}
                       title={c.taxable ? "Taxable — click to make non-taxable" : "Non-taxable — click to make taxable"}>
                  <input type="checkbox" checked={!!c.taxable} disabled={!canWrite || busy}
                         onChange={() => toggleCategoryTaxable(c)} />
                  {c.taxable ? "Taxable" : "Non-taxable"}
                </label>
              </span>
            ))}
            {!categories.length && <span style={{ color: T.textMuted, fontSize: 12 }}>No categories yet.</span>}
          </div>
          {canWrite && (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div>
                <label style={label}>New category name</label>
                <input style={input} value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder="e.g. Apparel" />
              </div>
              <div>
                <label style={label}>Parent category (optional)</label>
                <select style={input} value={newCategoryParent} onChange={(e) => setNewCategoryParent(e.target.value)}>
                  <option value="">— None —</option>
                  {categories.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
                </select>
              </div>
              <label style={{ ...label, display: "flex", alignItems: "center", gap: 6, marginBottom: 0 }}>
                <input type="checkbox" checked={newCategoryTaxable} onChange={(e) => setNewCategoryTaxable(e.target.checked)} />
                Taxable
              </label>
              <PrimaryButton disabled={busy || !newCategoryName.trim()} onClick={addCategory}>Add Category</PrimaryButton>
            </div>
          )}
        </div>
      )}

      <div style={{ ...card, marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <input
          value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or SKU…"
          style={{ ...input, flex: 1, minWidth: 220 }}
        />
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} style={{ ...input, width: 200 }}>
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
        </select>
        <label style={{ fontSize: 12, color: T.textSub, display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
          Active only
        </label>
      </div>

      <div style={{ ...card, padding: 0, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {th("name", "Name")}
              {th("sku", "SKU")}
              {th("category_id", "Category")}
              {th("selling_price", "Price")}
              {th("track_inventory", "Inventory")}
              {th("active", "Active")}
              <th style={{ padding: "8px 10px", borderBottom: `1px solid ${T.divider}` }} />
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => (
              <tr key={p.product_id} style={{ borderBottom: `1px solid ${T.divider}` }}>
                <td style={{ padding: "8px 10px", cursor: "pointer", fontWeight: 600 }} onClick={() => openEdit(p)}>{p.name}</td>
                <td style={{ padding: "8px 10px", color: T.textSub }}>{p.sku || "—"}</td>
                <td style={{ padding: "8px 10px", color: T.textSub }}>{categoryName(p.category_id)}</td>
                <td style={{ padding: "8px 10px" }}>{money(p.selling_price, currency)}</td>
                <td style={{ padding: "8px 10px", color: T.textSub }}>{p.track_inventory ? "Tracked" : "—"}</td>
                <td style={{ padding: "8px 10px" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: p.active ? T.green || "#2e7d32" : T.red, }}>
                    {p.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td style={{ padding: "8px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                  <GhostButton size="sm" onClick={() => openEdit(p)}>Edit</GhostButton>
                  {canWrite && p.active === 1 && (
                    <GhostButton size="sm" style={{ color: T.red, marginLeft: 6 }} onClick={() => deactivateProduct(p)}>Deactivate</GhostButton>
                  )}
                </td>
              </tr>
            ))}
            {!loading && !sorted.length && (
              <tr><td colSpan={7} style={{ padding: 20, textAlign: "center", color: T.textMuted }}>No products found.</td></tr>
            )}
            {loading && (
              <tr><td colSpan={7} style={{ padding: 20, textAlign: "center", color: T.textMuted }}>Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 20 }}>
          <div style={{ ...card, width: 640, maxHeight: "90vh", overflowY: "auto", background: isDark ? "#0f2436" : "#fff" }}>
            <h3 style={{ marginTop: 0 }}>{editing ? `Edit — ${editing.name}` : "New Product"}</h3>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={label}>Name *</label>
                <input style={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={!canWrite} />
              </div>
              <div>
                <label style={label}>Product Type</label>
                <select style={input} value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })} disabled={!canWrite}>
                  {PRODUCT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={label}>SKU</label>
                <input style={input} value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} disabled={!canWrite} />
              </div>
              <div>
                <label style={label}>Barcode</label>
                <input style={input} value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} disabled={!canWrite} />
              </div>
              <div>
                <label style={label}>Category</label>
                <select style={input} value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} disabled={!canWrite}>
                  <option value="">— None —</option>
                  {categories.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={label}>Brand</label>
                <input style={input} value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} disabled={!canWrite} />
              </div>
              <div>
                <label style={label}>Vendor</label>
                <input style={input} value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} disabled={!canWrite} />
              </div>
              <div>
                <label style={label}>Image URL</label>
                <input style={input} value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} disabled={!canWrite} />
              </div>
              <div>
                <label style={label}>Cost Price</label>
                <input type="number" style={input} value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} disabled={!canWrite} />
              </div>
              <div>
                <label style={label}>Selling Price</label>
                <input type="number" style={input} value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} disabled={!canWrite} />
              </div>
              <div>
                <label style={label}>Reorder Level</label>
                <input type="number" style={input} value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} disabled={!canWrite} />
              </div>
              <div>
                <label style={label}>Weight</label>
                <input type="number" style={input} value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} disabled={!canWrite} />
              </div>
              <div>
                <label style={label}>Tax Rate</label>
                <select style={input} value={form.tax_rate_id} onChange={(e) => setForm({ ...form, tax_rate_id: e.target.value })} disabled={!canWrite}>
                  <option value="">— None —</option>
                  {taxRates.map((t) => <option key={t.tax_rate_id} value={t.tax_rate_id}>{t.name} ({t.rate_pct}%)</option>)}
                </select>
              </div>
              <div>
                <label style={label}>Taxable</label>
                <select style={input} value={form.taxable} onChange={(e) => setForm({ ...form, taxable: e.target.value })} disabled={!canWrite}>
                  <option value="">Inherit from category</option>
                  <option value="1">Taxable</option>
                  <option value="0">Non-taxable</option>
                </select>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ ...label, display: "flex", alignItems: "center", gap: 6 }}>
                  <input type="checkbox" checked={form.track_inventory} onChange={(e) => setForm({ ...form, track_inventory: e.target.checked })} disabled={!canWrite} />
                  Track Inventory
                </label>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={label}>Description</label>
                <textarea style={{ ...input, minHeight: 60 }} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} disabled={!canWrite} />
              </div>
            </div>

            {editing && (
              <div style={{ marginTop: 18, borderTop: `1px solid ${T.divider}`, paddingTop: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <strong>Variants</strong>
                  {canWrite && !addingVariant && (
                    <GhostButton size="sm" onClick={() => setAddingVariant(true)}>+ Add Variant</GhostButton>
                  )}
                </div>
                {editing.created_at && (
                  <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>Created {fmtDateTime(editing.created_at)}</div>
                )}
                <div style={{ marginTop: 8 }}>
                  {(editing.variants || []).map((v) => (
                    <div key={v.variant_id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${T.divider}`, fontSize: 13 }}>
                      <span>{v.variant_name} {v.sku ? <span style={{ color: T.textSub }}>({v.sku})</span> : null}</span>
                      <span>{v.price != null ? money(v.price, currency) : "—"}</span>
                    </div>
                  ))}
                  {!(editing.variants || []).length && <div style={{ color: T.textMuted, fontSize: 12 }}>No variants.</div>}
                </div>
                {addingVariant && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                    <div>
                      <label style={label}>Variant Name *</label>
                      <input style={input} value={variantForm.variant_name} onChange={(e) => setVariantForm({ ...variantForm, variant_name: e.target.value })} />
                    </div>
                    <div>
                      <label style={label}>SKU</label>
                      <input style={input} value={variantForm.sku} onChange={(e) => setVariantForm({ ...variantForm, sku: e.target.value })} />
                    </div>
                    <div>
                      <label style={label}>Barcode</label>
                      <input style={input} value={variantForm.barcode} onChange={(e) => setVariantForm({ ...variantForm, barcode: e.target.value })} />
                    </div>
                    <div>
                      <label style={label}>Price</label>
                      <input type="number" style={input} value={variantForm.price} onChange={(e) => setVariantForm({ ...variantForm, price: e.target.value })} />
                    </div>
                    <div>
                      <label style={label}>Cost</label>
                      <input type="number" style={input} value={variantForm.cost} onChange={(e) => setVariantForm({ ...variantForm, cost: e.target.value })} />
                    </div>
                    <div>
                      <label style={label}>Weight</label>
                      <input type="number" style={input} value={variantForm.weight} onChange={(e) => setVariantForm({ ...variantForm, weight: e.target.value })} />
                    </div>
                    <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
                      <PrimaryButton disabled={busy || !variantForm.variant_name.trim()} onClick={addVariant}>
                        {busy ? "Saving…" : "Save Variant"}
                      </PrimaryButton>
                      <GhostButton onClick={() => { setAddingVariant(false); setVariantForm(emptyVariant); }}>Cancel</GhostButton>
                    </div>
                  </div>
                )}
              </div>
            )}

            {error && <div style={{ color: T.red, marginTop: 12 }}>{error}</div>}

            <div style={{ display: "flex", gap: 8, marginTop: 18, justifyContent: "flex-end" }}>
              {editing && canWrite && editing.active === 1 && (
                <GhostButton style={{ color: T.red, marginRight: "auto" }} onClick={() => deactivateProduct(editing)}>
                  Deactivate
                </GhostButton>
              )}
              <GhostButton onClick={closeModal}>Cancel</GhostButton>
              {canWrite && (
                <PrimaryButton disabled={busy} onClick={saveProduct}>
                  {busy ? "Saving…" : editing ? "Save Changes" : "Create Product"}
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
